import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import type { ApiError } from '@/services/api';
import { Button, Card, Skeleton } from './primitives';

export function PageHeader({ title, description, actions, breadcrumb }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; breadcrumb?: { label: string; to?: string }[] }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3 animate-fade-in sm:mb-6 sm:gap-4">
      <div className="min-w-0">
        {breadcrumb && (
          <nav aria-label="Breadcrumb" className="mb-1 flex flex-wrap items-center gap-1 text-xs text-muted">
            {breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <span aria-hidden>/</span>}
                {b.to ? (
                  <Link to={b.to} className="hover:text-text">
                    {b.label}
                  </Link>
                ) : (
                  <span>{b.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title = 'Nothing here yet', description, icon, action, className }: { title?: string; description?: ReactNode; icon?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center animate-fade-in', className)}>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-3 text-muted">{icon ?? <Inbox className="h-5 w-5" />}</div>
      <p className="text-sm font-semibold">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, className }: { error?: ApiError | Error | null; onRetry?: () => void; className?: string }) {
  const status = (error as ApiError | undefined)?.status;
  const title = status === 403 ? 'Access denied' : status === 404 ? 'Not found' : 'Something went wrong';
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-danger-soft text-danger">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted">{error?.message ?? 'Please try again.'}</p>
      {onRetry && status !== 403 && status !== 404 && (
        <Button variant="outline" size="sm" className="mt-4" icon={<RefreshCw className="h-3.5 w-3.5" />} onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

/** Renders loading / error / empty / content for a query. */
export function QueryState<T>({
  query,
  children,
  empty,
  isEmpty,
  skeleton,
}: {
  query: { data?: T; isLoading: boolean; isError: boolean; error: ApiError | null; refetch: () => unknown };
  children: (data: T) => ReactNode;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  skeleton?: ReactNode;
}) {
  if (query.isLoading) return <>{skeleton ?? <LoadingBlock />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.data === undefined) return null;
  if (isEmpty?.(query.data)) return <>{empty ?? <EmptyState />}</>;
  return <>{children(query.data)}</>;
}

export function LoadingBlock({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-5" aria-busy>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function StatCard({
  label,
  value,
  icon,
  hint,
  tone = 'primary',
  to,
  trend,
  loading,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  hint?: ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  to?: string;
  trend?: number;
  loading?: boolean;
}) {
  const toneCls = {
    primary: 'bg-primary-soft text-primary',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-info-soft text-info',
    neutral: 'bg-surface-3 text-muted',
  }[tone];
  const body = (
    <Card className={cn('h-full p-4 transition-all duration-200', to && 'hover:-translate-y-0.5 hover:border-primary hover:shadow-md')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-muted">{label}</p>
          {loading ? <Skeleton className="mt-2 h-7 w-16" /> : <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>}
        </div>
        {icon && <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', toneCls)}>{icon}</div>}
      </div>
      {(hint || trend !== undefined) && (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted">
          {trend !== undefined && (
            <span className={cn('inline-flex items-center gap-0.5 font-medium', trend >= 0 ? 'text-success' : 'text-danger')}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {Math.abs(trend)}%
            </span>
          )}
          {hint}
        </div>
      )}
    </Card>
  );
  return to ? (
    <Link to={to} className="block rounded-[var(--radius-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${label}: ${typeof value === 'number' || typeof value === 'string' ? value : ''}. Open list`}>
      {body}
    </Link>
  ) : (
    body
  );
}

export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="col-span-2 min-w-0 break-words">{children ?? '—'}</dd>
    </div>
  );
}
